import { FlagIcon } from './FlagIcon.component';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useStyles } from '../../../ActionsMenu/ActionsMenu.styles';
import { useState } from 'react';
import Menu from '@material-ui/core/Menu';
import {
  ExamTaskGroupSolutionScoreFlag,
  getExaminerFunctionLabel,
  getTooltipColorName,
} from '../../../../pages/Assessment/Assessment.types';
import TooltipOne from '../../../TooltipOne/TooltipOne.component';
import useSessionUser from '../../../../hooks/useSessionUser';
import UnavailableIcon from '../../../EditSession/UnavailableIcon.component';
import { ReactComponent as ActionMenuFlag } from '../../../../assets/icons/action-menu-flag.svg';
import { fillBlue } from '../../../../utils/contrastSvgAttributes';
import { AssignFlags } from '../../../../pages/Assessment/Modals/AssignFlags/AssignFlags.component';
import { FlagType } from './Flag.types';
import { IconButton } from '@material-ui/core';
import { useTabNavigationDetails } from '../../../../Context/TabNavigation.context';

export const FlagsTableItem: React.FC<{
  flags: ExamTaskGroupSolutionScoreFlag[];
  allAvailableFlags: FlagType[];
  examTaskGroupSolutionScoreId?: string;
  onSave: () => void;
  rowIndex?: number;
  isSolutionView?: boolean;
  setIsAssignFlagsModalOpen?: (isOpen: boolean) => void;
  disabled?: boolean;
}> = (props) => {
  const classes = useStyles();
  const maxNoOfDisplayedFlags = 3;

  const [anchorEl, setAnchorEl] = useState<SVGSVGElement | null>(null);
  const [assignFlagsOpen, setAssignFlagsOpen] = useState<boolean>(false);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { tabIndexForChildren, isDuringAction, activeRowIndex } = useTabNavigationDetails();

  const { user } = useSessionUser();

  const examinerId = user?.profile.sub;

  const getFlagTooltipTitle = (flag: ExamTaskGroupSolutionScoreFlag) => {
    if (flag.examinerId === examinerId) {
      return `${getTooltipColorName(flag)} znacznik flagi ${flag.comment ? `- ${flag.comment}` : ''}`;
    } else {
      return `${getTooltipColorName(flag)} znacznik flagi ${
        flag?.examinerFunction !== undefined ? '[' + getExaminerFunctionLabel(flag.examinerFunction, flag.isVerifier) + ']' : ''
      }`;
    }
  };

  const isDisabled = (flag: ExamTaskGroupSolutionScoreFlag) => {
    return flag.examinerId !== examinerId;
  };

  const isActionMenu = () => {
    return props.flags.length === 0;
  };

  const renderFlag = (flag: ExamTaskGroupSolutionScoreFlag): JSX.Element => (
    <TooltipOne title={getFlagTooltipTitle(flag)} placement="top" key={flag.flagId}>
      <span
        onClick={() => {
          if (props.disabled) return;
          setAssignFlagsOpen(true);
          props.setIsAssignFlagsModalOpen && props.setIsAssignFlagsModalOpen(true);
        }}
        onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            setAssignFlagsOpen(true);
            props.setIsAssignFlagsModalOpen && props.setIsAssignFlagsModalOpen(true);
          }
        }}
      >
        <div tabIndex={0} style={{ display: 'inline-flex', cursor: isDisabled(flag) ? 'not-allowed' : 'pointer' }}>
          <UnavailableIcon
            isVisible={isDisabled(flag)}
            styles={{
              position: 'absolute',
              height: '15px',
              width: '15px',
              transform: 'translate(15px, -3px)',
            }}
          />
          <FlagIcon key={flag.flagId} id={flag.flagDefinitionId} name={flag.name} color={flag.color} />
        </div>
      </span>
    </TooltipOne>
  );

  const assignFlagsModal = () => {
    return (
      <>
        {props.examTaskGroupSolutionScoreId && props.flags && assignFlagsOpen && (
          <AssignFlags
            examTaskGroupSolutionScoreId={props.examTaskGroupSolutionScoreId}
            assignedFlags={props.flags}
            allAvailableFlags={props.allAvailableFlags}
            onSave={() => {
              props.onSave();
              setAssignFlagsOpen(false);
              props.setIsAssignFlagsModalOpen && props.setIsAssignFlagsModalOpen(false);
            }}
          />
        )}
      </>
    );
  };

  const focusIfActive = (input: SVGSVGElement | null) => {
    if (isDuringAction && activeRowIndex === props.rowIndex) {
      input && input.focus();
    }
    return input;
  };

  const getActionMenuFlag = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <TooltipOne title={'Zarządzaj flagami'} placement="top">
          <span>
            {isActionMenu() && (
              <IconButton
                onClick={() => !props.disabled ? setAssignFlagsOpen(true) : undefined}
                style={{ padding: '5px' }}
                onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
                  if (event.key === 'Enter') {
                    if (!props.disabled) setAssignFlagsOpen(true);
                  }
                }}
              >
                <ActionMenuFlag
                  style={{ color: '#3C419A', cursor: 'pointer', paddingRight: '5px', padding: '0px' }}
                  aria-controls="action-menu"
                  stroke={fillBlue}
                  aria-haspopup="true"
                  aria-hidden="false"
                  aria-label="Zarządzaj flagami"
                  tabIndex={isDuringAction ? 0 : tabIndexForChildren}
                  ref={(input: SVGSVGElement | null) => focusIfActive(input)}
                />
              </IconButton>
            )}
          </span>
        </TooltipOne>
        {props.flags.map(renderFlag)}
        {assignFlagsModal()}
      </div>
    );
  };

  if (props.flags.length > 0) {
    if (props.flags.length <= maxNoOfDisplayedFlags) return <>{getActionMenuFlag()}</>;
    else
      return (
        <>
          {props.flags.slice(0, props.isSolutionView ? props.flags.length : maxNoOfDisplayedFlags - 1).map(renderFlag)}
          {props.isSolutionView && assignFlagsModal()}
          {!props.isSolutionView && (
            <>
              <TooltipOne title={`Zobacz więcej`} placement="top">
                <MoreHorizIcon
                  className={classes.actionIcon}
                  aria-controls="more-flags"
                  aria-haspopup="true"
                  aria-hidden="false"
                  onClick={handleClick}
                />
              </TooltipOne>
              <Menu open={open} anchorEl={anchorEl} onClose={handleClose}>
                {props.flags.map(renderFlag)}
              </Menu>
              {assignFlagsModal()}
            </>
          )}
        </>
      );
  } else
    return (
      <>
        {getActionMenuFlag()}
        {assignFlagsModal()}
      </>
    );
};
